"use client";

import { useEffect } from "react";

/**
 * Scroll-reveal fallback for browsers without CSS scroll-driven animations;
 * where `animation-timeline: view()` is supported, globals.css handles it and
 * this stays inert. The observer must only be armed inside useEffect so server
 * and first client render agree and no hydration mismatch occurs.
 */
export function ScrollReveal() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Under reduced motion CSS already shows everything immediately.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    // Where scroll-driven animations exist, CSS owns the reveal; JS must stay out.
    if (typeof CSS !== "undefined" && CSS.supports?.("animation-timeline: view()")) {
      return;
    }

    const targets = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reveal]:not(.is-revealed)"),
    );
    if (targets.length === 0) return;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            obs.unobserve(entry.target);
          }
        }
      },
      {
        rootMargin: "0px 0px -8% 0px",
        threshold: 0.08,
      },
    );

    for (const el of targets) obs.observe(el);

    // Client-side navigation can inject further targets after the initial pass.
    const mo = new MutationObserver(() => {
      const fresh = document.querySelectorAll<HTMLElement>(
        "[data-reveal]:not(.is-revealed)",
      );
      fresh.forEach((el) => obs.observe(el));
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      obs.disconnect();
      mo.disconnect();
    };
  }, []);

  return null;
}
